/**
 * The composer — task 5 (Tossker repaint), README §4 "Thread pane": one card
 * (radius 12, 1px `--border-default`, `--shadow-sm`) containing the
 * textarea, then a control row: a "Replying on <channel>" chip, "Enter sends
 * · Shift+Enter for a new line", and a primary "Send reply" with a `send`
 * icon. Enter sends, Shift+Enter newlines. Placeholder varies by channel and
 * liveness. A red strip appears above the card when that channel's transport
 * is down. A read-only thread replaces all of this with a sentence
 * explaining why.
 *
 * THE ATTACH ICON — task 8 (media images, 2026-09-04). The README's control
 * row shows a paperclip; it used to be omitted outright because no
 * attachment operation existed anywhere in the ops layer (a button that
 * opens nothing on click is the same lie the header's overflow icon would
 * have been — see ThreadHeader.tsx's own doc comment). That operation now
 * exists (`POST /ops/media`, `lib/ops.ts`'s `uploadMedia`, `send`'s
 * `mediaIds`), so the paperclip is built — but SMS-only (`channel ===
 * 'sms'`), matching the spec's access-rule table: chat and voice threads
 * carry no image path at all, and the absence there stays just as
 * deliberate as the whole icon used to be. Rendering it as an `<input
 * type="file">` behind a visible button, not a native drag-drop zone — no
 * design surface anywhere in this console models one, and a phone's share
 * sheet already routes through the file picker either way.
 *
 * Presentational — `ThreadPane` supplies `draft`/`onDraftChange` (bound to
 * the store's `draft` field), `onSend` (which calls the `send` operation),
 * and now `pendingMedia`/`onAttachFile`/`onRemovePending` (upload state and
 * the `uploadMedia` call itself live in `ThreadPane`, per the same rule —
 * this component never talks to the store or ops directly, so it can be
 * exercised in isolation for the placeholder/Enter/red-strip/attach tests).
 */
import { useRef } from 'react'
import type { KeyboardEvent } from 'react'
import type { Channel, Liveness } from '@communicator/shared'
import { Button, Icon } from '../ui'
import { CHANNEL_ICON, CHANNEL_LABEL, channelTileStyle } from '../rail/format'
import { composerPlaceholder } from './format'

/** One uploaded-and-pending-send image — `ThreadPane`'s own state, echoed back down as props. */
export interface PendingAttachment {
  /** The stored `MediaRef.id` — what `send`'s `mediaIds` will carry. */
  id: string
  /** The original filename, for the chip. Nothing else about the file is kept client-side once uploaded. */
  filename: string
}

/** `image/png,image/jpeg,image/gif,image/webp` — matches `IMAGE_MIMES` in packages/shared/src/media.ts exactly; the server sniffs magic bytes regardless, so this is a courtesy filter, not the real gate. */
export const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp'

/** MMS's practical ceiling — up to 10 images per send, matching `SendArgs.mediaIds`'s `.max(10)` in packages/shared/src/operations.ts. */
export const MAX_PENDING_ATTACHMENTS = 10

export interface ComposerProps {
  channel: Channel
  liveness: Liveness
  transportDown: boolean
  /** Non-null means read-only: render the sentence instead of the textarea. */
  readOnlyReason: string | null
  /** Retained for callers; nothing sets it true today — see isComposerSuppressed. */
  suppressed?: boolean
  /**
   * Voice threads offer two modes: a note to ourselves, or a text to the
   * caller. Absent on chat and sms, which have only one thing you can do.
   */
  mode?: 'note' | 'text'
  onModeChange?: (mode: 'note' | 'text') => void
  /** Why the Text tab cannot be used, when it cannot — e.g. no number on the call. Null when it can. */
  textUnavailable?: string | null
  draft: string
  onDraftChange: (value: string) => void
  onSend: () => void
  /**
   * A send is already in flight.
   *
   * The send button and the Enter key both refuse while this is true. Without
   * it, a slow connection meant the text sat in the box looking ignored, and
   * the natural response — tap it again — sent the customer the same message
   * twice.
   */
  sending?: boolean
  /** Task 8: images already uploaded for this thread, waiting to ride the next send. Empty/absent renders no chip row. */
  pendingMedia?: PendingAttachment[]
  /** Fires with the raw `File` the moment the file input resolves one — `ThreadPane` owns the pre-check, the `uploadMedia` call, and the resulting chip. Omitted entirely means no paperclip, regardless of channel. */
  onAttachFile?: (file: File) => void
  /** Removes one pending chip by id — the customer never sees it either way (nothing about an unsent attachment reaches them until `send`). */
  onRemovePending?: (id: string) => void
}

export function Composer({
  channel,
  liveness,
  transportDown,
  readOnlyReason,
  suppressed = false,
  draft,
  onDraftChange,
  onSend,
  sending = false,
  mode,
  onModeChange,
  textUnavailable = null,
  pendingMedia = [],
  onAttachFile,
  onRemovePending,
}: ComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  if (suppressed) return null

  if (readOnlyReason) {
    return (
      <div data-testid="composer-readonly" className="border-t border-subtle bg-sidebar px-4 py-3">
        <p className="text-[13px] text-mid">{readOnlyReason}</p>
      </div>
    )
  }

  const noteMode = mode === 'note'
  // A note goes nowhere near the transport, so a downed channel must not stop
  // somebody writing one — that is exactly when they most need to record what
  // was said.
  const canSend = (noteMode || !transportDown) && !sending && draft.trim().length > 0

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (canSend) onSend()
    }
    // Shift+Enter: no special handling needed — the browser's default
    // textarea behaviour already inserts a newline.
  }

  return (
    <div data-testid="composer" className="flex flex-col gap-2 border-t border-subtle bg-sidebar px-4 py-3">
      {transportDown && (
        <div role="alert" data-testid="transport-down-strip" className="rounded-control bg-bad-subtle px-3 py-2 text-[12px] text-bad">
          This channel's transport is down. Messages will not go through until it recovers.
        </div>
      )}
      {mode && onModeChange && (
        // The toggle sits ABOVE the box, not inside it: which of two things
        // enter will do is the first thing a dispatcher needs to know, and a
        // control buried beside the send button is read last.
        <div data-testid="composer-mode" role="tablist" aria-label="What enter does" className="flex items-center gap-1">
          {(['note', 'text'] as const).map((m) => {
            const active = mode === m
            const disabled = m === 'text' && textUnavailable !== null
            return (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={disabled}
                title={disabled ? textUnavailable ?? undefined : undefined}
                data-testid={`composer-mode-${m}`}
                onClick={() => onModeChange(m)}
                className={[
                  'inline-flex items-center gap-1 rounded-control px-2.5 py-1 text-[11.5px] font-semibold transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                ].join(' ')}
                style={
                  active
                    ? m === 'note'
                      ? { background: 'var(--tint-accent)', color: 'var(--on-tint-accent)' }
                      : { background: 'var(--brand-primary-strong)', color: 'var(--text-on-brand)' }
                    : { background: 'var(--surface-sunken)', color: 'var(--text-muted)' }
                }
              >
                <Icon name={m === 'note' ? 'pencil' : 'message-square'} size={12} />
                {m === 'note' ? 'Note' : 'Text'}
              </button>
            )
          })}
          <span className="ml-1 text-[11px] text-lo">
            {mode === 'note'
              ? 'Only your team sees this'
              : (textUnavailable ?? 'Goes to the caller as a text')}
          </span>
        </div>
      )}
      <div className="flex flex-col gap-2 rounded-card border border-default bg-card p-2.5 shadow-sm">
        {pendingMedia.length > 0 && (
          // Pending chips sit above the textarea, same reasoning as the mode
          // toggle above this card: what is about to go out is the first
          // thing to see, not something discovered after scrolling the
          // control row into view.
          <div className="flex flex-wrap items-center gap-1.5 px-1">
            {pendingMedia.map((m) => (
              <span
                key={m.id}
                data-testid="composer-attach-pending"
                className="inline-flex max-w-full items-center gap-1 rounded-[6px] bg-sunken px-2 py-1 text-[11px] text-mid [overflow-wrap:anywhere]"
              >
                <Icon name="paperclip" size={11} className="flex-none text-lo" />
                <span className="truncate">{m.filename}</span>
                <button
                  type="button"
                  aria-label={`Remove ${m.filename}`}
                  onClick={() => onRemovePending?.(m.id)}
                  className="flex-none text-lo hover:text-hi"
                >
                  <Icon name="x" size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
        <textarea
          data-testid="composer-textarea"
          /* Taller on a phone because the text is bigger there: iOS zooms any
             field under 16px, so the mobile rule in index.css bumps it — and
             52px, sized for 13px text, then clipped the descenders of a
             second line. The desktop height is unchanged. */
          className="h-[68px] w-full resize-none border-0 bg-transparent px-1 py-1 text-[13px] text-hi outline-none md:h-[52px]"
          placeholder={composerPlaceholder(channel, liveness)}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className="flex flex-wrap items-center gap-2 px-1 pb-0.5">
          {channel === 'sms' && onAttachFile && (
            // SMS-only — see this file's header comment. The input itself
            // stays off-screen (not `hidden`, which some browsers exclude
            // from the tab order AND from `.click()`-ability inconsistently
            // — `sr-only` keeps it focusable/clickable while invisible) and
            // the visible paperclip button forwards the click to it, the
            // standard "styled file input" pattern.
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES}
                data-testid="composer-attach-input"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  // Reset immediately so picking the SAME file again still
                  // fires onChange — the DOM only fires it on a value change.
                  e.target.value = ''
                  if (file) onAttachFile(file)
                }}
              />
              <button
                type="button"
                data-testid="composer-attach-btn"
                aria-label="Attach an image"
                disabled={pendingMedia.length >= MAX_PENDING_ATTACHMENTS}
                title={pendingMedia.length >= MAX_PENDING_ATTACHMENTS ? `Up to ${MAX_PENDING_ATTACHMENTS} images per message` : undefined}
                onClick={() => fileInputRef.current?.click()}
                className="flex-none text-lo hover:text-hi disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Icon name="paperclip" size={15} />
              </button>
            </>
          )}
          <span
            data-testid="composer-channel-chip"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-[5px] px-2 py-0.5 text-[10px] font-semibold"
            // Fix round 1 (coordinator review): this was hardcoded to
            // `--tint-primary` (teal) for every channel — wrong in all four
            // themes on an SMS thread (gold text/icon on a teal pill). The
            // chip's fill must vary by channel, same as the rail's own
            // channel tile (`channelTileStyle`, `RailRow.tsx`/`CallFloor.tsx`)
            // — never a fixed brand tint standing in for "channel colour".
            style={channelTileStyle(channel)}
          >
            <Icon name={CHANNEL_ICON[channel]} size={11} />
            Replying on {CHANNEL_LABEL[channel]}
          </span>
          <span className="text-[11px] text-lo">
            {noteMode ? 'Enter saves' : 'Enter sends'} · Shift+Enter for a new line
          </span>
          {/* The label follows the mode. A button reading "Send reply" while
              the box saves an internal note is the exact confusion the two
              colours exist to prevent — and the one that would matter, since
              somebody would eventually trust it. */}
          <Button
            data-testid="composer-send"
            variant="primary"
            disabled={!canSend}
            onClick={onSend}
            className="ml-auto"
          >
            <Icon name={noteMode ? 'pencil' : 'send'} size={13} />
            {noteMode ? 'Save note' : 'Send reply'}
          </Button>
        </div>
      </div>
    </div>
  )
}
